import { useCallback } from 'react';
import { BookOpen } from '../../../../icons';
import { readAll, type NotificationDto } from '../../../../api/notifications';
import { useWidgetData } from '../../../../hooks/useWidgetData';
import { fetchNotifications } from '../../../../api/notifications';
import { useNotificationStore } from '../../../../store/notificationStore';
import { useChatStore } from '../../../../store/chatStore';
import { widgetNotificationTarget } from '../notification-target';
import { invalidateWidgetCache } from '../../../../lib/widget-cache';
import { useMenuGate } from '../../../../lib/menu-gate';
import { WidgetCard } from '../WidgetCard';
import { useWidgetActions } from '../widget-context';
import { useNotices } from '../widget-queries';
// 읽은 공지를 흐리게 (2026-09-16) — 「읽음」과 「확인」을 가르는 판정은 한 곳에 둔다.
import { noticeDone } from '../notice-read';
import { agoText } from '../relative-time';

// 상대 시각은 board/relative-time.ts 한 곳에서 만든다 (2026-09-22) — 같은 판의 위젯이 같은 글자를 쓰게.
/**
 * 공지사항 위젯 (#2a) — 최신 공지.
 *
 * 관리 콘솔 "메뉴 관리"에서 게시판(menu_key='board')을 끄면 서버가 게시판 조회를 404 로
 * 막는다 → 여기서도 요청을 접고 "메뉴 관리에서 켜세요" 안내를 보여준다(2026-08-11).
 */
export function NoticesWidget() {
  const { go } = useWidgetActions();
  const gate = useMenuGate('board', '게시판');
  const { data, loading, error } = useNotices(gate.enabled);
  const rows = data ?? [];

  return (
    <WidgetCard
      title="공지사항"
      link={{ label: '더보기', onClick: () => go('/board') }}
      loading={loading}
      error={error}
      empty={rows.length === 0 ? '공지사항이 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="dw-rows">
        {rows.slice(0, 4).map((n) => {
          const done = noticeDone(n);
          return (
            <button type="button" key={n.id} className="dw-row" onClick={() => go('/board')}>
              {n.isImportant
                ? <span className="dw-chip dw-chip--new">필독</span>
                : <span className={`dw-row__bullet${done ? '' : ' dw-row__bullet--on'}`} aria-hidden />}
              <span className={`dw-row__text${done ? ' dw-row__text--read' : ''}`} title={n.title}>{n.title}</span>
              <span className="dw-row__meta">{agoText(n.postedAt)}</span>
            </button>
          );
        })}
      </div>
    </WidgetCard>
  );
}

/**
 * 알림 위젯 (#2b) — 최근 알림 + 모두 읽음.
 *
 * ★★★ **잘린 것이 있으면 말한다** (2026-09-16). 열 건을 받아 **네 줄만 그리고 나머지는
 *     말없이 버린다.** 안 읽은 것이 그 안에 있으면 **사용자는 그것이 있는 줄도 모른다.**
 * ★★ 세는 밑은 **머리에 이미 떠 있는 전체 안 읽음 수**(`unreadCount`)다 — 받아 온 열 건의
 *    합이 아니다. 열한 번째 알림이 안 읽음이어도 그 수에는 들어 있으므로, 이 한 줄은
 *    **목록 밖까지 정직하다.** (받아 온 것만 세면 「3건 더」라고 적고는 실제로 20건이 남아
 *    있는 일이 생긴다 — 그게 범위를 주장하는 거짓말이다.)
 * ★ 수를 모르거나(스토어가 아직 안 채워짐) 남은 것이 없으면 **줄을 아예 안 그린다.**
 * ★ 분류 이름표는 **넣지 않는다** — 줄을 두 줄로 펴면 제목이 잘려서, 사용자가 한 줄을
 *   지키기로 했다(2026-09-16 결정).
 */
export function NotificationsWidget() {
  const { go } = useWidgetActions();
  const setUnread = useNotificationStore((s) => s.setUnread);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  const { data, loading, error, reload } = useWidgetData<NotificationDto[]>(
    'notifications:recent',
    async () => (await fetchNotifications({ limit: 10 })).notifications,
  );
  const rows = data ?? [];

  const onReadAll = useCallback(async () => {
    try {
      await readAll();
      setUnread(0);
      // 알림 목록 캐시는 방금 무의미해졌다 — 무효화 후 재조회.
      invalidateWidgetCache('notifications:recent');
      reload();
    } catch {
      /* 실패해도 화면은 유지 — 다음 SSE/재조회로 자연 복구. */
    }
  }, [setUnread, reload]);

  /** 그리는 네 줄 안의 안 읽음 — 이것을 전체에서 빼면 「못 본 채 잘린 것」이 남는다. */
  const shownUnread = rows.slice(0, 4).filter((n) => !n.isRead).length;
  const hiddenUnread = Math.max(0, unreadCount - shownUnread);

  return (
    <WidgetCard
      title="알림"
      tail={unreadCount > 0 ? <span className="dw-chip dw-chip--new">{unreadCount}</span> : null}
      link={{ label: '모두 읽음', onClick: () => { void onReadAll(); } }}
      loading={loading}
      error={error}
      empty={rows.length === 0 ? '새 알림이 없습니다' : null}
      flush
    >
      <div className="dw-rows">
        {rows.slice(0, 4).map((n) => (
          <button
            type="button" key={n.id} className="dw-row"
            onClick={() => {
              /* ★ 알림이 가리키는 곳으로 (2026-09-15) — 종전에는 무엇이든 /board 였다(`notification-target.ts`). */
              const target = widgetNotificationTarget(n);
              if (target.chatRoomId) setActiveChat(target.chatRoomId);
              go(target.to);
            }}
          >
            <span className={`dw-row__bullet${n.isRead ? '' : ' dw-row__bullet--on'}`} aria-hidden />
            <span className={`dw-row__text${n.isRead ? ' dw-row__text--read' : ''}`} title={n.title}>{n.title}</span>
            <span className="dw-row__meta">{agoText(n.createdAt)}</span>
          </button>
        ))}
        {hiddenUnread > 0 && (
          <p className="dw-more">읽지 않은 알림 {hiddenUnread}건이 더 있습니다</p>
        )}
      </div>
    </WidgetCard>
  );
}

/**
 * 내 강의실 위젯 (#2b) — 사내 교육 과정.
 *
 * ⚠⚠ **2026-09-16 조사 — 이 아래 옛 주석은 틀렸다(고쳤다).**
 *
 * 옛 주석: 「아직 LMS 백엔드가 없다」. **위젯을 만든 2026-07-26 에는 맞았지만**,
 * **2026-08-27(`286907d3` · migration 135)에 사내 교육 백엔드가 들어왔다.** 지금 있는 것:
 *   · 표 `HrCourse`(법정/사내 · `dueOn` · `cycle`) · `HrCourseCompletion`
 *   · `GET /api/v1/hr/trainings/user/:userId` — **ADMIN 또는 본인**이라 직원이 자기 것을 볼 수 있다
 *   · 프론트 클라이언트 `api/hr-trainings.ts` 의 `fetchUserTrainings(userId)` — **이미 있다**
 *   · 같은 자료를 쓰는 화면이 이미 돈다: `me/record`(`MyRecordCardPage`)
 *
 * ★★★ **그래도 아직 값을 안 그린다 — 「없어서」가 아니라 「모양이 달라서」다.**
 *    시안(`docs/design/mockup-dashboard-2a.html`)은 「이어보기 **60%**」 진도율 바를 그리는데,
 *    서버 모델에는 **진도 필드가 없다**(이수/미이수 이진값뿐). 진도율을 그리려면 **지어내야 하고**,
 *    지어낸 진도율은 화면이 하는 거짓말 중 가장 나쁜 종류다.
 *
 * ★ **외부 LMS 연동은 여전히 없다** — `hr_integrations` 어댑터 목록에 교육 어댑터가 없고,
 *   env·문서 어디에도 대상 제품이 없다.
 *
 * ## ★★★ 2026-09-16 사용자 결정 — **「미개설」로 둔다**(정해졌다 · 대기 중이 아니다)
 *
 * 올린 선택지는 셋이었는데(① 범위를 줄여 실자료 연결 ② 위젯 제거 ③ 외부 LMS 지정),
 * 사용자는 **넷째**를 택했다: **자리는 두되 「미개설」이라고 명시한다.**
 *
 * ★★ 그래서 **`HrCourse` · `GET /hr/trainings/user/:userId` 에 «일부러» 잇지 않는다.**
 *    잇지 «못하는» 것이 아니라 **안 잇기로 정한 것**이다 — 다음 사람이 「자료가 있는데 왜 안 썼지」
 *    하고 연결하지 않도록 여기 적어 둔다.
 * ★ 값을 안 그리는 기술적 까닭도 그대로다: 시안의 진도율(「이어보기 60%」)을 받칠
 *   **진도 필드가 서버에 없다**(`HrCourseCompletion` 은 이수/미이수 이진값).
 *   없는 진도율을 지어내면 그것은 화면이 하는 거짓말 중 가장 나쁜 종류다.
 *
 * ★ **화면 문구는 «이 커밋에서» 「미개설」로 바꿨다**(위 ⚠ 가 넘긴 디자인 몫이 여기서 끝났다).
 *   종전 「연동 예정」은 **언젠가 온다는 약속**이라, 약속한 적 없는 것을 화면이 약속하고 있었다.
 *   서버 쪽 할 일은 **0건**이다.
 * ★ **갈 곳 없는 링크를 두지 않는다.** 「전체 ›」처럼 눌러도 아무 데도 못 가는 단추가
 *   제일 나쁘다 — 사용자는 그것을 고장으로 읽는다.
 * ★ 고르는 목록의 설명(`widget-registry`)도 함께 고쳤다 — 카드는 「미개설」인데 목록이
 *   「사내 교육 진도」라고 하면 **진도를 기대하고 얹은 사람이 빈 카드를 받는다.**
 */
export function ClassroomWidget() {
  return (
    <WidgetCard title="내 강의실">
      <div className="dw-placeholder">
        <BookOpen size={22} aria-hidden />
        <div className="dw-placeholder__t">미개설</div>
        <div className="dw-placeholder__s">사내 교육 과정이 아직 열리지 않았습니다</div>
      </div>
    </WidgetCard>
  );
}
